import { MAX_PER_KIND, toggleChoice } from '../../pages/recipes/taxonomy';

/**
 * Grouped multi-select chips for a controlled vocabulary (cuisines, courses).
 * Each chip is a toggle button (`aria-pressed`); once `max` are chosen the
 * unchosen chips are disabled until one is removed.
 */
export function ChoiceChipPicker<T extends string>({
  id,
  label,
  hint,
  groups,
  labels,
  value,
  onChange,
  max = MAX_PER_KIND,
}: {
  id: string;
  label: string;
  hint?: string;
  groups: ReadonlyArray<{ label: string; keys: readonly T[] }>;
  labels: Record<T, string>;
  value: readonly T[];
  onChange: (next: T[]) => void;
  max?: number;
}) {
  const full = value.length >= max;
  return (
    <fieldset className="flex flex-col gap-2" aria-describedby={`${id}-hint`}>
      <legend className="text-sm font-medium text-ink">{label}</legend>
      <p id={`${id}-hint`} className="-mt-1 text-xs text-muted">
        {hint ? `${hint} ` : ''}
        {value.length} of {max} chosen.
      </p>
      {groups.map((group, gi) => (
        <div key={group.label} className="flex flex-col gap-1">
          <span id={`${id}-group-${gi}`} className="text-xs font-semibold text-muted uppercase">
            {group.label}
          </span>
          <ul aria-labelledby={`${id}-group-${gi}`} className="flex flex-wrap gap-2">
            {group.keys.map((key) => {
              const on = value.includes(key);
              return (
                <li key={key}>
                  <button
                    type="button"
                    aria-pressed={on}
                    disabled={!on && full}
                    onClick={() => onChange(toggleChoice(value, key, max))}
                    className={`min-h-9 rounded-full px-3 text-sm transition-colors disabled:opacity-40 ${
                      on
                        ? 'bg-brand text-white'
                        : 'border border-line bg-surface text-ink hover:bg-brand-soft hover:text-brand'
                    }`}
                  >
                    {on ? <span aria-hidden>✓ </span> : null}
                    {labels[key]}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </fieldset>
  );
}
